import * as React from "react";
import { useState, useEffect } from "react";
import Input from "@/components/atomics/Input";
import Direction from "@/components/atomics/direction";
import StepLoad from "@/components/atomics/step-load";
import SelectInput from "@/components/atomics/select-input";
import { inputData } from "@/data/input-data";
import { useNavigate } from "react-router-dom";
import { useCollection } from "@/context/CollectionContext";
import Help from "@/components/atomics/help";
import { helpDescription } from "@/data/help";
import MotionModal from "@/components/atomics/motion-modal";
import CheckBoxField from "@/components/atomics/check-box-field";
import { useSnackbar } from "notistack";
import {
  minCollectionItemPrice,
  minCollectionItemPriceWhitelist,
} from "@/hooks/useCollectionContract";
import Guide from "@/components/molecules/guide/guide";
import { whiteListGuideData } from "@/data/guide";

export default function CreateCollectionSecondStep() {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {
    formData: contextFormData,
    setFormData: setContextFormData,
    ipfsUrls,
  } = useCollection();
  const [showHelp, setShowHelp] = useState(false);
  const [helpTitle, setHelpTitle] = useState("");
  const [showWhitelistGuide, setShowWhitelistGuide] = useState(false);
  const [formData, setFormData] = useState({
    ...inputData.reduce(
      (acc, input) => ({
        ...acc,
        [input.label]: input.value,
      }),
      {}
    ),
    enable_whitelist: false,
    enable_mintlimit_perWallet: false,
    SBT: false, // false
    ...contextFormData,
  });

  const [checkboxStates, setCheckboxStates] = useState({
    "MINT LIMIT": false,
    "TIME LIMIT": false,
  });

  useEffect(() => {
    const areIpfsUrlsValid = ipfsUrls.cover && ipfsUrls.icon && ipfsUrls.nft;
    if (!areIpfsUrlsValid) {
      navigate("/collection/create/1");
    }
    setContextFormData(formData);
  }, [formData, setContextFormData, ipfsUrls, navigate]);

  const handleInputChange = (label: string, value: string | boolean) => {
    let processedValue = value;
    let newPrice: number | null = null;
    if (label === "MINT LIMIT" && typeof value === "string") {
      // Remove any non-numeric characters
      processedValue = value.replace(/[^\d]/g, "");
      // Allow empty string during typing
      processedValue = processedValue;
    }

    if (label === "ROYALTY_%" && typeof value === "string") {
      // Remove any non-numeric characters
      processedValue = value.replace(/[^\d]/g, "");
      // Ensure value is between 0-100
      const numValue = parseInt(processedValue as string) || 0;
      processedValue = numValue > 100 ? "100" : processedValue;
    }

    if (label === "TIME LIMIT" && typeof value === "string") {
      processedValue = value;
    }

    if (label === "PRICE IN TON" && typeof value === "string") {
      // Allow only numbers and one decimal point
      processedValue = value.replace(/[^\d.]/g, "");
      // Ensure only one decimal point
      const parts = processedValue.split(".");
      if (parts.length > 2) {
        processedValue = parts[0] + "." + parts.slice(1).join("");
      }
      // Check minimum price
      const numValue = parseFloat(processedValue) || 0;
      const actualMinPrice = formData.enable_whitelist
        ? minCollectionItemPriceWhitelist
        : minCollectionItemPrice;
      if (numValue < Number(actualMinPrice)) {
        snackbar.enqueueSnackbar(
          `Mint price must be bigger than ${actualMinPrice} TON`,
          {
            SnackbarProps: {
              onClick: () => {
                snackbar.closeSnackbar();
              },
            },
            variant: "error",
            autoHideDuration: 1000,
          }
        );
      }
    }

    if (label === "SBT") {
      processedValue = value;
    }

    if (label === "enable_whitelist") {
      // Require to setup new min price
      const currentTonPrice = Number(formData["PRICE IN TON"]);
      const whitelistPrice = Number(minCollectionItemPriceWhitelist);
      if (value && currentTonPrice < Number(minCollectionItemPriceWhitelist)) {
        newPrice = whitelistPrice;
      }
      // If whitelist disabled, return min price
      if (!value && currentTonPrice === whitelistPrice) {
        newPrice = Number(minCollectionItemPrice);
      }
      processedValue = value;
    }

    if (label === "enable_mintlimit_perWallet") {
      processedValue = value;
    }
    newPrice
      ? setFormData(prev => ({
          ...prev,
          [label]: processedValue,
          ["PRICE IN TON"]: newPrice,
        }))
      : setFormData(prev => ({
          ...prev,
          [label]: processedValue,
        }));
  };

  const handleAgreementChange = (label: string, isAgreed: boolean) => {
    setCheckboxStates(prev => ({
      ...prev,
      [label]: isAgreed,
    }));
  };

  useEffect(() => {
    const combinedData = {
      ...formData,
      "MINT LIMIT_ENABLED": checkboxStates["MINT LIMIT"],
      "TIME LIMIT_ENABLED": checkboxStates["TIME LIMIT"],
    };
    setContextFormData(combinedData);
  }, [formData, checkboxStates, setContextFormData]);

  const isPriceValid = () => {
    const price = parseFloat(formData["PRICE IN TON"]);
    const royalty = parseInt(formData["ROYALTY_%"]);
    const mintLimit = parseInt(formData["MINT LIMIT"]);
    const minPrice = formData.enable_whitelist
      ? Number(minCollectionItemPriceWhitelist)
      : Number(minCollectionItemPrice);
    return (
      price >= minPrice && royalty >= 0 && royalty <= 100 && mintLimit >= 0
    );
  };

  return (
    <div className="w-full flex flex-col justify-center items-center pt-[20px] pb-[70px] relative">
      <MotionModal isOpen={showHelp}>
        <Help
          className="w-full bg-black"
          onClose={() => setShowHelp(false)}
          onOk={() => setShowHelp(false)}
          title={helpTitle}
          description={helpDescription[helpTitle]}
        />
      </MotionModal>
      <MotionModal isOpen={showWhitelistGuide}>
        <Guide
          guideData={whiteListGuideData}
          onClose={() => setShowWhitelistGuide(false)}
        />
      </MotionModal>
      <header className="flex flex-col justify-center text-[32px] text-center">
        <div className="relative">
          <span>STEP_2</span>
          <div
            className="absolute top-[50%] left-[calc(50%+85px)] translate-x-[-50%] translate-y-[-50%] text-[#0099FE] hover:cursor-pointer"
            onClick={() => {
              setShowHelp(true);
              setHelpTitle("Step2");
            }}
          >
            <span>?</span>
          </div>
        </div>
        <span> SETUP COLLECTION</span>
      </header>
      <div className="w-full flex flex-col gap-[20px] pt-[30px] pb-[22px] pl-[30px] pr-[30px]">
        {inputData.map((input, index) => (
          <Input
            key={index}
            label={input.label}
            placeholder={input.placeholder}
            essential={input.essential}
            type={
              input.label === "TIME LIMIT"
                ? "datetime-local"
                : input.label === "MINT LIMIT" || input.label === "ROYALTY_%"
                  ? "number"
                  : input.type
            }
            inputMode={
              input.label === "MINT LIMIT" || input.label === "ROYALTY_%"
                ? "numeric"
                : input.label === "PRICE IN TON"
                  ? "decimal"
                  : undefined
            }
            pattern={
              input.label === "MINT LIMIT" || input.label === "ROYALTY_%"
                ? "[0-9]*"
                : undefined
            }
            step={input.label === "PRICE IN TON" ? 0.1 : undefined}
            value={formData[input.label]}
            includeAgreement={
              input.label === "TIME LIMIT" || input.label === "MINT LIMIT"
            }
            hint={
              input.label === "PRICE IN TON"
                ? `Price includes contract holdings, 
                ~0.04 without whitelist and ~0.08 with it
                use min price for free`
                : undefined
            }
            onChange={e => handleInputChange(input.label, e.target.value)}
            onAgreementChange={isAgreed =>
              handleAgreementChange(input.label, isAgreed)
            }
            onHelpClick={() => {
              setShowHelp(true);
              setHelpTitle(input.label);
            }}
          />
        ))}
        <SelectInput
          label="SBT"
          essential={false}
          value={formData["SBT"]}
          onChange={e => handleInputChange("SBT", e)}
          onHelpClick={() => {
            setShowHelp(true);
            setHelpTitle("SBT");
          }}
        />
        <div className="flex flex-col gap-[10px] mt-[70px]">
          <CheckBoxField
            label="WHITELIST"
            checked={formData.enable_whitelist}
            onChange={value => handleInputChange("enable_whitelist", value)}
            onHelpClick={() => {
              setShowWhitelistGuide(!showWhitelistGuide);
            }}
            isBeta={true}
          />
        </div>
        {formData.enable_whitelist && (
          <div className="flex flex-col gap-[10px] mt-[10px]">
            <CheckBoxField
              label="1 MINT PER WALLET"
              checked={formData.enable_mintlimit_perWallet}
              onChange={value =>
                handleInputChange("enable_mintlimit_perWallet", value)
              }
              onHelpClick={() => {
                setShowHelp(true);
                setHelpTitle("MINT LIMIT PER WALLET");
              }}
              isBeta={true}
            />
          </div>
        )}
      </div>
      <StepLoad currentIndex={1} totalIndex={3} />
      <div className="w-full relative flex justify-center mt-[14px]">
        <span
          className={`text-[32px] ${
            isPriceValid()
              ? "text-[#0099FE] hover:cursor-pointer"
              : "text-[#2B2B2B] hover:cursor-not-allowed"
          }`}
          onClick={() => {
            isPriceValid() && navigate("/collection/create/3");
          }}
        >
          {" "}
          NEXT{" "}
        </span>
        <div
          className={`absolute top-[50%] right-[60px] translate-y-[-50%] w-[17px] h-[29px] ${
            isPriceValid()
              ? "text-[#0099FE] hover:cursor-pointer"
              : "text-[#484848] hover:cursor-not-allowed"
          }`}
          onClick={() => {
            isPriceValid() && navigate("/collection/create/3");
          }}
        >
          <Direction
            className="w-full h-full"
            color={isPriceValid() ? "#0099FE" : "#2B2B2B"}
          />
        </div>
        <div
          className="absolute top-[50%] left-[60px] translate-y-[-50%] rotate-[180deg] w-[17px] h-[29px] text-[#0099FE] hover:cursor-pointer"
          onClick={() => {
            navigate("/collection/create/1");
          }}
        >
          <Direction className="w-full h-full" color="#0099FE" />
        </div>
      </div>
    </div>
  );
}
